import React, { useCallback, useContext } from "react";
import { CtaCustomData } from "../CtasContainer/CtasContainer";
import { PluginDataContext } from "../../../../pluginsInfrastructure/PluginDataContextProvider";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { getProductCtaInfo, getCtaOnClickHandler } from "../../utils/ctaHelpers";
import { analyticsPageData } from "../../../../utils/analyticsConfig";
import { CtaButton } from "./CtaButton";
import { MpvProvider } from "../../../../utils/MpvProvider";
import UploadFlowCta from "./UploadFlowCta";
import { useExperimentsContext } from "../../../../contexts/ExperimentsProvider";
import { RevisedDesignFirstFlowContext } from "../../../../utils/abTests/abTests";

export interface StudioCtaProps {
  customData: CtaCustomData;
}

const StudioCta = (props: StudioCtaProps) => {
  const { pluginData } = useContext(PluginDataContext);
  const {
    customData: {
      flow: { type, url, context },
      isPrimaryCta,
      buttonIndex,
      showDescription,
      isDesignUploaded,
      isLastCta,
      hasMoreThanOneStudioFlow,
    },
  } = props;
  const { useExperiment } = useExperimentsContext();
  const revisedDesignFirstFlowVariation = useExperiment(RevisedDesignFirstFlowContext);
  const { mpv, ctaOverrideEntries, productBuilderOptions } = useContext(PageStaticDataContext);
  const { temporarilyOutOfStock, prices, initialPrices, compatibleRanges } = useContext(
    BffDataContext
  );
  const {
    selections,
    selectedQuantity,
    clientSideStateInitialized,
    initialSelections,
    initialQuantity,
  } = useContext(ProductContext);

  // The siteflow action is used to determine if we go to full bleed upload or just start from scratch
  const siteflowAction =
    hasMoreThanOneStudioFlow && type === "studio:blank" ? "studioblank" : "design";

  const mpvProvider = new MpvProvider(mpv);
  const uploadFlowMerchandisingDetail = mpvProvider.getUploadFlowMerchandisingDetails();
  if (
    uploadFlowMerchandisingDetail &&
    !temporarilyOutOfStock &&
    revisedDesignFirstFlowVariation !== "test"
  ) {
    return <UploadFlowCta customData={props.customData} siteflowAction={siteflowAction} />;
  }

  const ctaEnabled = clientSideStateInitialized && !temporarilyOutOfStock;

  const { actualCtaText, actualCtaDescription, actualCtaIcon } = getProductCtaInfo(
    pluginData,
    ctaOverrideEntries,
    type,
    isDesignUploaded,
    isPrimaryCta
  );

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) =>
      getCtaOnClickHandler({
        event,
        mpv,
        selections: selections || {},
        initialSelections,
        pageSection: analyticsPageData.pageSection,
        position: buttonIndex + 1,
        url,
        linkType: type,
        initialQuantity,
        selectedQuantity,
        prices,
        productBuilderOptions,
        initialPrices,
        flowContexts: context,
        isDesignUploaded,
        compatibleRanges,
      }),
    [
      selections,
      initialSelections,
      analyticsPageData,
      buttonIndex,
      type,
      initialQuantity,
      selectedQuantity,
      mpv,
      prices,
      productBuilderOptions,
      initialPrices,
      getCtaOnClickHandler,
    ]
  );

  return (
    <CtaButton
      onClickHandler={onClickHandler}
      disabled={!ctaEnabled}
      linkProperties={{
        href: url,
        openInNewTab: false,
      }}
      buttonKey={type}
      text={actualCtaText}
      description={showDescription ? actualCtaDescription : ""}
      iconComponent={actualCtaIcon}
      textButtonProps={{
        skin: isPrimaryCta ? "primary" : "secondary",
      }}
      addBottomSpace={!isLastCta}
      pageSection={analyticsPageData.pageSection}
      position={buttonIndex}
    />
  );
};

export default StudioCta;
